
import React from 'react';
import { motion } from 'framer-motion';
import { fadeIn, slideIn, getReducedMotionVariants } from '../utils/motion';
import { EnhancedCard } from '@/components/ui/enhanced-card';
import { images } from '@/assets/images';

interface MeetTheFounderPageProps {
  reducedMotion: boolean;
}

const MeetTheFounderPage: React.FC<MeetTheFounderPageProps> = ({ reducedMotion }) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 w-full">
      {/* Left Column - Founder Photo */}
      <motion.div
        variants={getReducedMotionVariants(slideIn('left', 0, 0.6), reducedMotion)}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        className="flex flex-col items-center justify-center space-y-4"
      >
        <div className="relative">
          <img
            src={images.philFounder}
            alt="Phil Russell, Founder and CEO of EquiForge"
            className="w-48 h-48 md:w-56 md:h-56 rounded-full object-cover shadow-xl border-4 border-white/10"
            loading="lazy"
          />
          <div className="absolute inset-0 rounded-full bg-gradient-to-t from-primary/20 to-transparent"></div>
        </div>
        <div className="text-center">
          <h3 className="text-xl font-bold">Phil Russell</h3>
          <p className="text-muted-foreground">Founder & CEO</p>
        </div>
      </motion.div>

      {/* Right Column - Founder Story */}
      <motion.div
        variants={getReducedMotionVariants(slideIn('right', 0.2, 0.6), reducedMotion)}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        className="lg:col-span-2 flex flex-col justify-center space-y-6"
      >
        <EnhancedCard className="p-6 h-full">
          <div className="space-y-6">
            <div>
              <h2 className="text-3xl font-bold mb-4">Meet the Founder</h2>
              <div className="space-y-4 text-muted-foreground">
                <p className="text-lg leading-relaxed">
                  Phil Russell founded EquiForge with a simple but powerful vision: to democratize 
                  access to sophisticated real estate analysis tools that were previously only 
                  available to industry professionals. Today, EquiForge has grown into a comprehensive 
                  platform with 10+ integrated tools serving thousands of homebuyers.
                </p>
                
                <p>
                  During his time as a New Home Consultant where he helped over 500 families realize their dream of home ownership, 
                  Phil witnessed firsthand how homebuyers were making one of life's biggest financial 
                  decisions with incomplete information and outdated tools. This experience drove the 
                  development of everything from advanced calculators to AI-powered assistance.
                </p>
                
                <p>
                  "Every homebuyer deserves to understand exactly what they're getting into," Phil explains. 
                  "Our mission is to provide transparency, accuracy, and confidence in every real estate 
                  transaction through advanced analytics and AI-powered insights."
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t">
              <div className="space-y-2">
                <h4 className="font-semibold text-primary">Platform Impact</h4>
                <p className="text-sm text-muted-foreground">
                  10,000+ analyses completed across 10+ integrated tools
                </p>
              </div>
              <div className="space-y-2">
                <h4 className="font-semibold text-primary">Experience</h4>
                <p className="text-sm text-muted-foreground">
                  6+ years in new home sales and 30+ years in innovative technology
                </p>
              </div>
              <div className="space-y-2">
                <h4 className="font-semibold text-primary">Mission</h4>
                <p className="text-sm text-muted-foreground">
                  Bringing transparency and precision to homebuying decisions
                </p>
              </div>
            </div>
          </div>
        </EnhancedCard>
      </motion.div>
    </div>
  );
};

export default MeetTheFounderPage;
